import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  User, Building2, Plug, Bell,
  Settings, LogOut, FileText, CreditCard, Workflow
} from 'lucide-react';

const PROFILE_MENU_ITEMS = [
  { path: '/profile', label: 'My Profile', icon: User },
  { path: '/company', label: 'Company Intelligence', icon: Building2 },
  { path: '/reports', label: 'Reports', icon: FileText },
  { path: '/integrations', label: 'Integrations', icon: Plug },
  { path: '/notifications', label: 'Notifications', icon: Bell, badge: true },
  { path: '/settings/billing', label: 'Billing & Plan', icon: CreditCard, adminOnly: true },
  { path: '/settings/workflows', label: 'Workflow Studio', icon: Workflow },
  { path: '/settings', label: 'Settings', icon: Settings },
];

const ProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/notifications/unread-count`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.count || 0);
        }
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const getInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="profile-dropdown-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="relative cursor-pointer rounded-full focus:outline-none focus:ring-2 focus:ring-[#0D1F3C] focus:ring-offset-2"
      >
        {user?.avatar ? (
          <img
            src={user.avatar}
            alt="User avatar"
            className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-slate-800 object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0D1F3C] text-xs font-bold text-white ring-2 ring-white">
            {getInitials()}
          </div>
        )}
        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-slate-800 rounded-full"></div>
      </button>

      {/* Dropdown Menu - Opens Downward, Anchored Right */}
      {isOpen && (
        <div
          data-testid="profile-dropdown-menu"
          role="menu"
          className="absolute right-0 top-full mt-3 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 py-2 z-[60] animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
              {user?.name || 'My Account'}
            </p>
            {user?.email && (
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                {user.email}
              </p>
            )}
          </div>

          <div className="py-1">
            {PROFILE_MENU_ITEMS.filter((item) => !item.adminOnly || user?.role === 'admin').map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const showBadge = item.badge && unreadCount > 0;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`profile-menu-${item.path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    active
                      ? 'bg-slate-100 text-[#0D1F3C] font-semibold'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{item.label}</span>
                  {showBadge && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Divider */}
          <div className="h-px bg-slate-200 dark:bg-slate-700 my-1"></div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            data-testid="profile-menu-logout"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
